/**
 * History + Insight types — Phase 4.1 + 4.2.
 *
 * Mirrors the server shape returned by:
 *   - GET /v1/history/:child_id/insights (InsightCardData)
 *   - GET /v1/history/:child_id (HistoryPage with HistoryItem[])
 *
 * Kept in /types/ (not in lib/api/) so both the API client and the UI
 * components can import without circular deps.
 */

// ----------------------------------------------------------------------------
// Insight
// ----------------------------------------------------------------------------

/** One row in the "Topik favorit" list. */
export interface InsightTemaRow {
  tema: string;
  n_approved: number;
}

export interface InsightTopActivity {
  activity: string;
  count: number;
}

/** The "fastest approved" entry. Null if no approve swipe exists. */
export interface InsightFastest {
  name: string;
  timeToDecideMs: number;
}

/** Full insight card data — 6 facts in 30-day window. */
export interface InsightCardData {
  windowDays: number;
  totalPrinted: number;
  totalSelected: number;
  topTema: InsightTemaRow[];
  activityCounts: InsightTopActivity[];
  topActivity: InsightTopActivity | null;
  fastest: InsightFastest | null;
  undoCount: number;
  /** ISO timestamp when this data was aggregated. */
  generatedAt: string;
}

// ----------------------------------------------------------------------------
// History list
// ----------------------------------------------------------------------------

/**
 * Status badge enum — mirrors the server's 5-state mapper (see
 * apps/api/src/lib/history.ts). The client renders one of 5 labels:
 *  - 'printed'             → "Selesai · Dicetak"    (green)
 *  - 'shared_pdf'          → "Selesai · Disimpan PDF" (green)
 *  - 'shared_wa'           → "Selesai · Dikirim WA"  (green)
 *  - 'selesai_belum_dicetak' → "Selesai (belum dicetak)" (gray)
 *  - 'belum_dicetak'       → "Belum dicetak"          (gray)
 */
export type HistoryStatus =
  | 'printed'
  | 'shared_pdf'
  | 'shared_wa'
  | 'selesai_belum_dicetak'
  | 'belum_dicetak';

export interface HistoryItem {
  id: number;
  worksheetId: number;
  name: string;
  thumbnailUrl: string | null;
  /** Full worksheet preview; falls back to thumbnailUrl when unavailable. */
  imageUrl: string | null;
  pdfUrl: string | null;
  activityLabel: string | null;
  ageLabel: string | null;
  /** 14 PAUD-specific tema names (sorted by sort_order). Empty if no mapping. */
  temaNames: string[];
  approvedAt: string;
  completedAt: string | null;
  printStatus: HistoryStatus;
  completionFeedback: string | null;
  /** YYYY-MM-DD slice of approved_at (for date grouping on the client). */
  approvedDate: string;
}

export interface HistoryPage {
  items: HistoryItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  /** All selected worksheets grouped by activity for the current range. */
  activityCounts: ActivityCount[];
  statusCounts: HistoryStatusCounts;
  hasMore: boolean;
}

export interface ActivityCount {
  activityLabel: string;
  count: number;
}

export interface HistoryStatusCounts {
  printed: number;
  needsPrint: number;
}

export type HistoryRange = 'all' | 'today' | '7d' | 'older';

// ----------------------------------------------------------------------------
// Status badge labels
// ----------------------------------------------------------------------------

/** Human-readable label for a status badge (Indonesian, Y1). */
export const STATUS_BADGE_LABEL: Record<HistoryStatus, string> = {
  printed: 'Selesai · Dicetak',
  shared_pdf: 'Selesai · Disimpan PDF',
  shared_wa: 'Selesai · Dikirim WA',
  selesai_belum_dicetak: 'Selesai (belum dicetak)',
  belum_dicetak: 'Belum dicetak',
};
